import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ME_QUERY_KEY } from "@/lib/auth";
import { dispararDescarga } from "@/lib/descargas";

/**
 * RolMiembro es el catálogo cerrado de roles asignables a un miembro de una obra
 * (espejo de obras/dto.go: ADMIN_OBRA/CUALIFICADO/AUTORIZADO/CONSULTA). El flag
 * global `es_admin` NO es un rol por obra, así que no aparece aquí.
 */
export type RolMiembro = "ADMIN_OBRA" | "CUALIFICADO" | "AUTORIZADO" | "CONSULTA";

/**
 * ROL_MIEMBRO_LABEL traduce el catálogo cerrado de roles por obra a su etiqueta legible.
 * Vive aquí, y no en una feature, porque lo comparten la configuración de la obra y la ficha
 * del usuario (#238) y la regla del repo prohíbe que una feature importe de otra.
 */
export const ROL_MIEMBRO_LABEL: Record<RolMiembro, string> = {
  ADMIN_OBRA: "Administrador de obra",
  CUALIFICADO: "Cualificado",
  AUTORIZADO: "Autorizado",
  CONSULTA: "Consulta",
};

/** ROL_MIEMBRO_OPCIONES es ese catálogo en el orden en que lo ofrecen los selectores. */
export const ROL_MIEMBRO_OPCIONES: { value: RolMiembro; label: string }[] = (
  ["ADMIN_OBRA", "CUALIFICADO", "AUTORIZADO", "CONSULTA"] as const
).map((value) => ({ value, label: ROL_MIEMBRO_LABEL[value] }));

/**
 * Obra es la representación de salida de una obra (GET /api/v1/obras). El
 * `codigo_obra` lo asigna el servidor (correlativo anual); la baja es soft-delete
 * (deja de listarse). Espejo de obras.ObraResponse.
 */
export type Obra = {
  id_obra: number;
  codigo_obra: string;
  nombre: string;
  descripcion?: string | null;
  activa: boolean;
  version: number;
  updated_at: string;
};

/** ObraInput es el payload de alta/edición (POST/PUT /api/v1/obras). */
export type ObraInput = {
  nombre: string;
  descripcion: string | null;
  activa: boolean;
};

/**
 * AutorizacionObra es la autorización LOTO de una persona EN una obra, sin repetir su
 * identidad: ya la lleva la fila que la contiene (el miembro de la obra o la pertenencia
 * de la ficha del usuario). Espejo de obras.AutorizacionObraResponse (#238): una sola
 * forma para la autorización embebida, la sirva la pantalla de obra o la de usuarios.
 */
export type AutorizacionObra = {
  autorizado_loto: boolean;
  vigente: boolean;
  fecha_autorizacion?: string | null;
  fecha_caducidad?: string | null;
  emitida_por?: string | null;
  alcance?: string | null;
  observaciones?: string | null;
};

/**
 * Miembro es un usuario asignado a una obra con su rol (GET
 * /api/v1/obras/{id}/miembros). Espejo de obras.MiembroResponse: viaja con la
 * identidad del usuario (nombre_usuario + persona), su rol en la obra y su autorización
 * LOTO en ella (#238: embebida, para pintarla sin una segunda llamada). `id_persona`
 * permite conceder autorizaciones LOTO (que son por persona) eligiendo entre los
 * miembros (#161).
 */
export type Miembro = {
  id_usuario: number;
  id_persona: number;
  nombre_usuario: string;
  nombre: string;
  apellidos: string;
  email?: string | null;
  autorizacion?: AutorizacionObra | null;
  rol: RolMiembro;
  /** Documentación acreditativa de la persona (#435); null si no la ha aportado. */
  documento_acreditativo?: DocumentoAcreditativo | null;
};

/**
 * DocumentoAcreditativo son los metadatos publicables de la documentación acreditativa de una
 * persona (#435). Espejo de obras.DocumentoAcreditativoResponse.
 */
export type DocumentoAcreditativo = {
  nombre: string;
  mime: string;
  tamano: number;
  subido_en: string;
  subido_por_nombre: string;
};

/**
 * Candidato es un usuario asignable a la obra devuelto por la búsqueda de candidatos
 * (GET /obras/{id}/miembros/candidatos?q=). Espejo de obras.CandidatoResponse:
 * deliberadamente mínimo (id + nombre visible), sin email ni estados.
 */
export type Candidato = {
  id_usuario: number;
  nombre: string;
};

/** MiembroInput es el payload de asignación de un miembro (POST /obras/{id}/miembros). */
export type MiembroInput = {
  id_usuario: number;
  rol: RolMiembro;
};

/**
 * Autorizacion es la habilitación LOTO de una persona en la obra (GET
 * /api/v1/obras/{id}/autorizaciones). Espejo de obras.AutorizacionResponse: viaja
 * por persona (id_persona + nombre) con su ventana de vigencia. Las fechas son
 * cadenas AAAA-MM-DD (columnas DATE) o ausentes.
 */
export type Autorizacion = {
  id_persona: number;
  nombre: string;
  apellidos: string;
  autorizado_loto: boolean;
  vigente: boolean;
  fecha_autorizacion?: string | null;
  fecha_caducidad?: string | null;
  emitida_por?: string | null;
  alcance?: string | null;
  observaciones?: string | null;
};

/**
 * AutorizacionInput es el payload de habilitación LOTO por (persona, obra) (POST
 * /obras/{id}/autorizaciones). Upsert por persona: reenviar sobreescribe. Las
 * fechas viajan como AAAA-MM-DD o null.
 */
export type AutorizacionInput = {
  id_persona: number;
  autorizado_loto: boolean;
  vigente: boolean;
  fecha_autorizacion: string | null;
  fecha_caducidad: string | null;
  emitida_por: string | null;
  alcance: string | null;
  observaciones: string | null;
};

// --- Obras (colección, ADMIN global) ---

export const OBRAS_QUERY_KEY = ["obras"] as const;

/**
 * obrasQueryOptions lista las obras vivas ordenadas por nombre. La colección es
 * ADMIN-only en el backend (RequireAdmin); la UI oculta además el acceso. Se
 * refresca invalidando la clave tras cada mutación, igual que los maestros.
 */
export const obrasQueryOptions = queryOptions({
  queryKey: OBRAS_QUERY_KEY,
  queryFn: () => api.get<Obra[]>("/obras"),
});

// `enabled` permite a consumidores que solo necesitan el nombre de una obra puntual (el
// conmutador y la gestión de miembros para un admin que aún no la tiene en su /me) pedir la
// colección solo cuando de verdad hace falta, sin lanzarla en cada carga.
export function useObras(options?: { enabled?: boolean }) {
  return useQuery({ ...obrasQueryOptions, enabled: options?.enabled ?? true });
}

/**
 * invalidarColeccionObras refresca lo que depende del catálogo de obras tras un alta, una
 * edición o una baja: la colección y, desde #252 (D8), el `me`. La obra activa recordada y el
 * menú lateral se resuelven contra `me.obras`, así que sin invalidarlo el admin seguiría
 * viendo en el menú una obra recién borrada —o no vería la recién creada— hasta reiniciar
 * la sesión.
 */
function invalidarColeccionObras(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: OBRAS_QUERY_KEY });
  void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
}

export function useCreateObra() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ObraInput) => api.post<Obra>("/obras", input),
    onSuccess: () => invalidarColeccionObras(queryClient),
  });
}

export function useUpdateObra() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: ObraInput }) =>
      api.put<Obra>(`/obras/${id}`, input),
    onSuccess: () => invalidarColeccionObras(queryClient),
  });
}

export function useDeleteObra() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del<void>(`/obras/${id}`),
    onSuccess: () => invalidarColeccionObras(queryClient),
  });
}

// --- Miembros de una obra (ADMIN o ADMIN_OBRA de la obra) ---

/** miembrosQueryKey namespaquea la lista de miembros por obra. */
export function miembrosQueryKey(obraId: number) {
  return ["obra", obraId, "miembros"] as const;
}

export function miembrosQueryOptions(obraId: number) {
  return queryOptions({
    queryKey: miembrosQueryKey(obraId),
    queryFn: () => api.get<Miembro[]>(`/obras/${obraId}/miembros`),
  });
}

export function useMiembros(obraId: number) {
  return useQuery(miembrosQueryOptions(obraId));
}

export function useAsignarMiembro(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: MiembroInput) => api.post<Miembro>(`/obras/${obraId}/miembros`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: miembrosQueryKey(obraId) }),
  });
}

export function useRetirarMiembro(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (idUsuario: number) => api.del<void>(`/obras/${obraId}/miembros/${idUsuario}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: miembrosQueryKey(obraId) }),
  });
}

/**
 * MIN_CANDIDATOS_QUERY es la longitud mínima de la búsqueda de candidatos: por debajo
 * no se consulta al servidor (espejo de la regla del backend, que con <2 caracteres
 * devuelve lista vacía para no volcar el directorio).
 */
export const MIN_CANDIDATOS_QUERY = 2;

/** candidatosQueryKey namespaquea la búsqueda de candidatos por obra y término. */
export function candidatosQueryKey(obraId: number, q: string) {
  return ["obra", obraId, "miembros-candidatos", q] as const;
}

/**
 * useCandidatos busca usuarios asignables a la obra (GET /miembros/candidatos?q=,
 * gate RequireObraAdmin). Solo consulta con q de al menos MIN_CANDIDATOS_QUERY
 * caracteres (enabled); el debounce del tecleo lo aporta el formulario.
 */
export function useCandidatos(obraId: number, q: string) {
  const termino = q.trim();
  return useQuery({
    queryKey: candidatosQueryKey(obraId, termino),
    queryFn: () =>
      api.get<Candidato[]>(`/obras/${obraId}/miembros/candidatos?q=${encodeURIComponent(termino)}`),
    enabled: termino.length >= MIN_CANDIDATOS_QUERY,
  });
}

// --- Autorizaciones LOTO por obra (ADMIN o gestor de la obra) ---

// La LECTURA del listado de autorizaciones de una obra se quedó sin consumidor cuando #238
// llevó la gestión a la ficha del usuario y a la lista de miembros (que ya trae embebida la
// autorización de cada uno). El endpoint sigue existiendo en el servidor; aquí no queda
// código muerto (#281 D7). Lo que sí se conserva es la CONCESIÓN, que usa la configuración
// de obra.
export function useSetAutorizacion(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AutorizacionInput) =>
      api.post<Autorizacion>(`/obras/${obraId}/autorizaciones`, input),
    // La autorización viaja embebida en cada miembro: invalidar el listado de miembros es lo
    // que repinta la obra tras conceder o revocar.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: miembrosQueryKey(obraId) }),
  });
}

// --- Destinatarios de avisos LOTO por obra (ADMIN o ADMIN_OBRA de la obra) ---

/**
 * DestinatarioAviso es un destinatario de los avisos LOTO de una obra (GET
 * /api/v1/obras/{id}/avisos). Espejo de obras.DestinatarioAvisoResponse: el chip se
 * identifica por `id_destinatario` (para el quitar) y muestra el `email`. `id_usuario`
 * viaja si el chip nació de un miembro de la obra (referencia informativa).
 */
export type DestinatarioAviso = {
  id_destinatario: number;
  email: string;
  id_usuario?: number | null;
};

/**
 * DestinatarioAvisoInput es el payload de alta de un destinatario (POST
 * /obras/{id}/avisos): el email y, opcionalmente, el id del miembro del que nació.
 */
export type DestinatarioAvisoInput = {
  email: string;
  id_usuario?: number | null;
};

/**
 * AvisosResponse es la respuesta de GET /obras/{id}/avisos (#205). Espejo de
 * obras.AvisosResponse: la lista de destinatarios (`destinatarios`) más el estado del
 * motor SMTP del servidor (`motor_configurado`), para que la pestaña «Avisos» advierta
 * de que, sin motor, los avisos no se enviarán.
 */
export type AvisosResponse = {
  motor_configurado: boolean;
  destinatarios: DestinatarioAviso[];
};

/** avisosQueryKey namespaquea la lista de destinatarios de avisos por obra. */
export function avisosQueryKey(obraId: number) {
  return ["obra", obraId, "avisos"] as const;
}

export function avisosQueryOptions(obraId: number) {
  return queryOptions({
    queryKey: avisosQueryKey(obraId),
    queryFn: () => api.get<AvisosResponse>(`/obras/${obraId}/avisos`),
  });
}

export function useAvisos(obraId: number) {
  return useQuery(avisosQueryOptions(obraId));
}

export function useAgregarAviso(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: DestinatarioAvisoInput) =>
      api.post<DestinatarioAviso>(`/obras/${obraId}/avisos`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: avisosQueryKey(obraId) }),
  });
}

export function useQuitarAviso(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (idDestinatario: number) =>
      api.del<void>(`/obras/${obraId}/avisos/${idDestinatario}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: avisosQueryKey(obraId) }),
  });
}

// --- Documentación acreditativa (#435) ---

/**
 * DOCUMENTO_ACREDITATIVO_ACCEPT son los tipos que admite el servidor (D10): PDF, JPEG y PNG. El
 * servidor los detecta por CONTENIDO; esto solo filtra el selector de archivos.
 */
export const DOCUMENTO_ACREDITATIVO_ACCEPT = "application/pdf,image/jpeg,image/png";

/** NOMBRE_DOCUMENTO_RESPALDO es el nombre de la descarga si el servidor no manda ninguno. */
export const NOMBRE_DOCUMENTO_RESPALDO = "documento-acreditativo";

/** SubirDocumentoVars identifica a quién se sube el documento y el archivo elegido. */
export type SubirDocumentoVars = { idUsuario: number; archivo: File };

/**
 * formularioDocumento compone el multipart de la subida: el archivo va en el campo
 * `documento`, el que lee el servidor.
 */
export function formularioDocumento(archivo: File): FormData {
  const form = new FormData();
  // El nombre va explícito: es el que el servidor sanea y guarda como nombre original.
  form.append("documento", archivo, archivo.name);
  return form;
}

/**
 * invalidarTrasDocumento refresca todo lo que depende de la documentación acreditativa tras
 * subirla, sustituirla o borrarla: el listado de usuarios (ficha), los miembros de CUALQUIER
 * obra en caché —el documento es global a la persona (D2), así que su estado cambia en todas
 * sus obras— y el `me`, del que cuelgan el aviso al trabajador y su `puede_registrar`. Lo
 * comparten las dos familias de rutas (ficha y configuración de obra).
 */
export function invalidarTrasDocumento(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: ["usuarios"] });
  void queryClient.invalidateQueries({
    predicate: (q) => q.queryKey[0] === "obra" && q.queryKey[2] === "miembros",
  });
  void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
}

/**
 * descargarDocumento baja el binario de la ruta dada y lo guarda con el nombre que fija el
 * servidor en su Content-Disposition (o el de respaldo). Requiere conexión: no se cachea ni
 * se encola.
 */
export async function descargarDocumento(ruta: string): Promise<void> {
  const { blob, nombreArchivo } = await api.getBlob(ruta);
  dispararDescarga(blob, nombreArchivo ?? NOMBRE_DOCUMENTO_RESPALDO);
}

function rutaDocumentoMiembro(obraId: number, idUsuario: number): string {
  return `/obras/${obraId}/miembros/${idUsuario}/documento-acreditativo`;
}

/**
 * useSubirDocumentoMiembro sube (o sustituye) la documentación acreditativa de un miembro de la
 * obra con PUT multipart (ADMIN o ADMIN_OBRA; el servidor exige que sea miembro, 404 si no).
 */
export function useSubirDocumentoMiembro(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ idUsuario, archivo }: SubirDocumentoVars) =>
      api.putForm<DocumentoAcreditativo>(
        rutaDocumentoMiembro(obraId, idUsuario),
        formularioDocumento(archivo),
      ),
    onSuccess: () => invalidarTrasDocumento(queryClient),
  });
}

/** useBorrarDocumentoMiembro borra la documentación acreditativa de un miembro de la obra. */
export function useBorrarDocumentoMiembro(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (idUsuario: number) => api.del<void>(rutaDocumentoMiembro(obraId, idUsuario)),
    onSuccess: () => invalidarTrasDocumento(queryClient),
  });
}

/**
 * useDescargarDocumentoMiembro descarga la documentación acreditativa de un miembro. Es una
 * mutación (y no una query) porque es una acción puntual del usuario con estado de «en curso»,
 * no una lectura que cachear; no invalida nada.
 */
export function useDescargarDocumentoMiembro(obraId: number) {
  return useMutation({
    mutationFn: (idUsuario: number) => descargarDocumento(rutaDocumentoMiembro(obraId, idUsuario)),
  });
}
